import type { Ref } from 'react'
import {
  Button,
  Choice,
  Field,
  MoneyInput,
  Question,
  SettingBlock,
  Toggle,
} from '../ui'
import { coverFeeSays, HOW_MUCH, money } from './donate-page-helpers'
import { SplitIntoPlan } from './SplitIntoPlan'

/**
 * The donate page's amount question: a suggested figure or one typed, the
 * bounds said where the figure was typed, and the card cost decided beside it.
 */
export function DonateAmountStep({
  stepRef,
  orgId,
  to,
  suggestedAmountsCents,
  minGiftCents,
  maxGiftCents,
  typed,
  onTyped,
  pending,
  boundsId,
  forLabel,
  donorName,
  donorEmail,
  coverFee,
  onCoverFee,
  onAmount,
  onPledgeComplete,
  standing,
  offersPlan,
}: {
  stepRef: Ref<HTMLDivElement>
  orgId: string
  /** Who the gift is to, as the page names them. */
  to: string
  suggestedAmountsCents: number[]
  minGiftCents: number
  maxGiftCents: number
  typed: string
  onTyped: (typed: string) => void
  /** The typed figure in cents, or null where it reads as none. */
  pending: number | null
  boundsId: string
  forLabel: string | null
  donorName: string
  donorEmail: string
  coverFee: boolean
  onCoverFee: (on: boolean) => void
  onAmount: (cents: number) => void
  onPledgeComplete: (checkoutId: string) => void
  /**
   * The figure is given each month or year. Each is charged by the card the
   * giver leaves, which covers no card cost, and a large one is not split
   * into a plan: it is already paid over time.
   */
  standing: boolean
  /** Kiosks take one gift now and offer no pledge or installment path. */
  offersPlan: boolean
}) {
  const inBounds = (cents: number) =>
    cents >= minGiftCents && cents <= maxGiftCents
  const outOfBounds = pending !== null && !inBounds(pending)
  /* Above the ceiling is not the same answer as below the floor. A figure under
     the floor is a typo and is refused; one over it is usually a commitment,
     and the org takes those as a pledge paid in installments — so the page
     offers that rather than stopping at a refusal. */
  const overCeiling =
    offersPlan && !standing && pending !== null && pending > maxGiftCents

  return (
    <Question ref={stepRef} ask={HOW_MUCH} appearance="page">
      {/* An org that suggests nothing leaves the row out rather than drawing an
        empty one: the typed figure is then the whole question. */}
      {suggestedAmountsCents.length > 0 && (
        <Choice
          ariaLabel={HOW_MUCH}
          appearance="chip"
          value={
            suggestedAmountsCents.some((c) => String(c / 100) === typed)
              ? typed
              : ''
          }
          options={suggestedAmountsCents.map((c) => ({
            value: String(c / 100),
            label: money(c),
          }))}
          onValueChange={onTyped}
        />
      )}
      <Field
        label={suggestedAmountsCents.length > 0 ? 'Another amount' : 'Amount'}
      >
        <MoneyInput
          value={typed}
          min={minGiftCents / 100}
          max={maxGiftCents / 100}
          aria-invalid={outOfBounds || undefined}
          aria-describedby={outOfBounds ? boundsId : undefined}
          onChange={(e) => onTyped(e.target.value)}
        />
      </Field>
      {/* A figure outside the bounds says so where it was typed: a commit that
        can only go quiet reads as a broken page. Bound to the field, so it is
        part of what the control says about itself rather than a sentence
        beside it. */}
      {overCeiling && pending !== null ? (
        <SplitIntoPlan
          orgId={orgId}
          amountCents={pending}
          forLabel={forLabel}
          donorName={donorName}
          donorEmail={donorEmail}
          describedId={boundsId}
          onCheckoutComplete={onPledgeComplete}
        />
      ) : outOfBounds ? (
        <p className="donate__fine" id={boundsId} role="alert">
          A gift is taken between {money(minGiftCents)} and{' '}
          {money(maxGiftCents)}.
        </p>
      ) : null}
      {/* A setting, not a field: it changes what the charge IS, so it is
        decided with the figure it is a share of. Off by default — the donor
        opts in, having seen what it adds. A figure over the ceiling is
        arranged as a plan, which charges no card cost, so the setting goes
        with it. */}
      {!overCeiling && !standing && (
        <SettingBlock
          title="Cover the card processing cost"
          description={coverFeeSays(
            pending !== null && inBounds(pending) ? pending : null,
            to,
          )}
        >
          <Toggle
            checked={coverFee}
            onChange={onCoverFee}
            label="Cover the card processing cost"
            hideLabel
          />
        </SettingBlock>
      )}
      <Button
        variant="secondary"
        disabled={pending === null || !inBounds(pending)}
        onClick={() => pending !== null && onAmount(pending)}
      >
        Continue
      </Button>
    </Question>
  )
}
